import { inject, Injectable } from '@angular/core';
import { ChartDatasetConfig, ChartDataService } from '@app/core/services/charts/chart-data.service';
import { CartesianTickOptions, ChartData, ChartOptions, Plugin, TooltipOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { splitTextByLines } from '@app/shared/utils/split-label-by-words';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { FloatingPopupBarChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-bar-chart.service';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { ILocalizationKey } from '@app/core/interfaces/localization';
import { ILoggerNoiseHistogram } from '@app/api/interfaces/chart-data/logger-noise-histogram';
import { IFpLoggerNoiseHistogramChartConfig } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-histogram/interfaces/fp-logger-noise-histogram-chart-config';
import { createCategoryXTicksPlugin } from '@app/core/services/charts/plugins/category-x-ticks.plugin';
import { ChartThemeService } from '@app/core/services/charts/chart-theme.service';

@Injectable()
export class FpLoggerNoiseHistogramChartService {
  protected floatingPopupBarChartService = inject(FloatingPopupBarChartService);
  protected chartDataService = inject(ChartDataService);
  protected translate: TranslateService = inject(TranslateService);
  protected colorManagementService = inject(ColorManagementService);
  protected chartThemeService = inject(ChartThemeService);

  private noDataLocalizationKey: ILocalizationKey = _(`@@shared-no-data-value-placeholder`);

  public getXTicksPlugin(): Plugin {
    return createCategoryXTicksPlugin({
      isMajorTick: label => Number(label) % 5 === 0,
      isMinorTick: label => Number(label) % 1 === 0,
      majorTickLength: 8,
      minorTickLength: 4,
      color: this.chartThemeService.getColors().tickColor,
    });
  }

  public generateOptions(chartConfig: IFpLoggerNoiseHistogramChartConfig): ChartOptions {
    const xLabel = this.buildXLabel(chartConfig);
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    const options = this.floatingPopupBarChartService.generateOptions({
      chartTranslation: { yLabel: _('@@shared-count'), xLabel, titleText: null },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
      interactionsEnabled: true,
      hoverBarBackgroundEnabled: false,
      categoryAxisTicksEnabled: false,
    });

    this.applyXTicks(options);
    return options;
  }

  public generateMinimizedOptions(chartConfig: IFpLoggerNoiseHistogramChartConfig): ChartOptions {
    const xLabel = this.buildXLabel(chartConfig);
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    const options = this.floatingPopupBarChartService.generateOptions({
      chartTranslation: { yLabel: _('@@shared-count'), xLabel, titleText: null },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
      interactionsEnabled: false,
      hoverBarBackgroundEnabled: false,
      categoryAxisTicksEnabled: false,
    });

    this.applyXTicks(options);
    return options;
  }

  private applyXTicks(options: ChartOptions): void {
    const xScale = options.scales?.['x'];
    if (!xScale) return;
    const xTicks = xScale.ticks as _DeepPartialObject<CartesianTickOptions> | undefined;
    if (!xTicks) return;
    xTicks.autoSkip = false;
    xTicks.maxTicksLimit = undefined;
    xTicks.callback = function (value) {
      const label = this.getLabelForValue(Number(value));
      const num = parseFloat(label);
      return Number.isFinite(num) && num % 5 === 0 ? String(num) : '';
    };
  }

  private buildXLabel(chartConfig: IFpLoggerNoiseHistogramChartConfig): string {
    if (chartConfig.spread === null) {
      return this.translate.instant(_('@@shared-db_label'));
    }
    return this.translate.instant(_('@@chart-histogram-x-axis-spread-label'), {
      I18N_VAR_VALUE: chartConfig.spread,
    });
  }

  private dataConverter(data: ILoggerNoiseHistogram): number | null {
    return data.count ?? null;
  }

  private generateDatasetConfig(): ChartDatasetConfig<ILoggerNoiseHistogram>[] {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getMainColor = (item: ILoggerNoiseHistogram, index: number) => {
      return this.colorManagementService.getChartDefaultColor();
    };

    return [
      {
        label: '',
        tooltipLabel: '',
        type: 'bar',
        dataValue: (data: ILoggerNoiseHistogram) => this.dataConverter(data),
        borderColor: (item, index) => getMainColor(item, index).toRgbString(),
        backgroundColor: (item, index) => {
          const color = getMainColor(item, index);
          return this.colorManagementService.getColorInstanceWithOpacity(color, 0.4).toRgbString();
        },
        hoverBackgroundColor: (item, index) => {
          const color = getMainColor(item, index);
          return this.colorManagementService.getColorInstanceWithOpacity(color, 0.7).toRgbString();
        },
        borderWidth: 1,
        barPercentage: 1.0,
        categoryPercentage: 1.0,
        minBarLength: 4,
      },
    ];
  }

  public getDataForChart(data: ILoggerNoiseHistogram[]): ChartData {
    const datasetConfigs = this.generateDatasetConfig();
    const labels = data.map(d => String(d.noiseDecibel));
    return {
      labels,
      datasets: this.chartDataService.generateDatasets<ILoggerNoiseHistogram>([...data], datasetConfigs),
    };
  }

  public getMinimizedDataForChart(data: ILoggerNoiseHistogram[]): ChartData {
    const datasetConfigs = this.generateDatasetConfig();
    const labels = data.map(d => String(d.noiseDecibel));
    return {
      labels,
      datasets: this.chartDataService.generateDatasets<ILoggerNoiseHistogram>([...data], datasetConfigs),
    };
  }

  private getTooltipOptions(): _DeepPartialObject<TooltipOptions> {
    return {
      callbacks: {
        title: () => {
          const text = this.translate.instant(_('@@chart-histogram-tooltip-title'));
          return splitTextByLines(text, 40);
        },
        label: tooltipItem => {
          const noDataText: string = this.translate.instant(this.noDataLocalizationKey);
          const amplitude = tooltipItem.label ?? noDataText;
          const count = tooltipItem.raw as number | null;
          const countFormatted =
            count !== null ? this.floatingPopupBarChartService.formatChartNumber(count, 0, 0) : noDataText;
          return this.translate.instant(_('@@chart-histogram-tooltip-xy'), {
            I18N_VAR_X_VALUE: amplitude,
            I18N_VAR_Y_VALUE: countFormatted,
            I18N_VAR_SEPARATOR: '  ',
          });
        },
      },
    };
  }
}
